import { useUpdateDielec } from "@/api/hooks/useDielce";
import { extractErrorMessage } from "@/api/client";
import { hlaskaNahrady, resolveAutoEdge, STRANY_HRAN } from "@/lib/autoEdge";
import {
  hranyDoUpdate,
  toHrany,
  toZaoblenie,
  zaoblenieDoUpdate,
  type ZaoblenieState,
} from "@/lib/hrany";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { STAV_ZLA_SIRKA } from "@/lib/stavHran";
import {
  HRANA_FARBY,
  HranaOdkaz,
  QUICK_HRUBKY,
  ZAOBLENIE_FARBA,
  hrubkaBucket,
  jeNahradnaHrubka,
  useEdgeInfo,
} from "@/components/dielec/DielecVisual";
import type { EdgeState, HranyState } from "@/components/dielec/HranyEditor";
import type { Dielec } from "@/api/schemas";

/**
 * Mini-lišta hrán — 48 px, ktoré cestujú so scrollom namiesto 325 px pásu
 * (KOLO 67).
 *
 * Pás detailu je v ľavom stĺpci a nehýbe sa; keď ho user odscrolluje
 * z dohľadu, editor hrán príde za ním v tejto lište. Hrany sú v podstate
 * jediné, na čo sa detail používa, a potrebujú ~48 px, nie 325.
 *
 * **Nie je to `DielecDetailPanel` v kompaktnom režime**: panel má debounced
 * auto-save celého stavu (rozmery + hrany + slot), takže dve namontované
 * inštancie toho istého dielca by si navzájom prepisovali PATCH-e. Lišta
 * preto nemá lokálny stav polí — číta hrany z `dielec` (server state) a
 * **ukladá hneď**. Prepnutie hrany je jedno kliknutie, takže jeden PATCH na
 * klik je presne správne; debounce by tu nič nešetril. Undo funguje samo,
 * `useUpdateDielec` kroky zapisuje (KOLO 61).
 *
 * Preklad hrán do PATCH-u je v `lib/hrany.ts` a preklad mm → hrana
 * v `lib/autoEdge.ts` — obidva zdieľané s plným detailom, aby neexistovali
 * dve verzie tej istej logiky.
 *
 * Masív (KOLO 168, `dielec.masiv`) sa neolepuje — lišta namiesto hrán
 * ponúka zaoblenie strán a ukladá len jeho polia.
 */
export interface HranyListaProps {
  zakazkaId: string;
  dielec: Dielec;
}

/** Rozmer mini nákresu — len obrázok, klikacie prvky sú mimo neho. */
const MINI_W = 56;
const MINI_H = 32;

export function HranyLista({
  zakazkaId,
  dielec,
}: HranyListaProps): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const hrany = toHrany(dielec);
  const zaoblenie = toZaoblenie(dielec);
  const slotId = dielec.material_slot_id ?? null;
  const zMm = dielec.z_mm != null ? parseFloat(String(dielec.z_mm)) : null;

  function uloz(nove: HranyState): void {
    updateMutation
      .mutateAsync({ id: dielec.id, data: hranyDoUpdate(nove) })
      .catch((err: unknown) => {
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní hrany",
          description: extractErrorMessage(err),
        });
      });
  }

  /** Masív: prepni zaoblenie strany — PATCH len s poliami zaoblenia. */
  function prepniZaoblenie(key: keyof ZaoblenieState): void {
    const nove = { ...zaoblenie, [key]: !zaoblenie[key] };
    updateMutation
      .mutateAsync({ id: dielec.id, data: zaoblenieDoUpdate(nove) })
      .catch((err: unknown) => {
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní zaoblenia",
          description: extractErrorMessage(err),
        });
      });
  }

  function toggle(key: keyof HranyState): void {
    const e = hrany[key];
    const zapnuta = e.auto || !!e.id;
    uloz({ ...hrany, [key]: { id: null, auto: !zapnuta } });
  }

  /** Chip hrúbky — rovnaká semantika ako `quickAssign` v `DielecVisual`. */
  async function priradHrubku(
    key: keyof HranyState,
    hranaMm: string,
  ): Promise<void> {
    if (!slotId) {
      toast({
        variant: "destructive",
        title: "Dielec nemá materiál",
        description: "Hrúbku hrany vieme priradiť až po výbere materiálu.",
      });
      return;
    }
    try {
      const edge = await resolveAutoEdge(slotId, zMm, hranaMm);
      if (!edge) {
        // Tiché preskočenie by znamenalo, že si user myslí, že hranu nastavil
        toast({
          variant: "destructive",
          title: `Hrana ${hranaMm} mm nie je v ponuke materiálu`,
          description: "Pridaj ju v materiáli cez + Pridať z cenníka.",
        });
        return;
      }
      uloz({ ...hrany, [key]: { id: edge.id, auto: false } });
      if (edge.nahradena) {
        toast({
          title: "Použitá najbližšia hrúbka",
          description: hlaskaNahrady(hranaMm, edge.hrubkaMm),
        });
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri priraďovaní hrany",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <div
      /* Pozicovanie NIE je tu — lištu vkladá editor do `sticky h-0` obálky
         s absolútnym obsahom, aby jej objavenie neposunulo tabuľku. Tento
         komponent je len 48 px vysoký pás. */
      className="flex h-12 items-center gap-3 overflow-hidden rounded-lg border bg-background px-3 shadow-[0_2px_6px_rgba(0,0,0,0.06)]"
      data-testid="hrany-lista"
    >
      <div className="min-w-0 shrink-0">
        <div className="font-mono text-xs font-semibold leading-4">
          {dielec.kod ?? "—"}
        </div>
        <div
          className="max-w-[110px] truncate text-[10px] leading-4 text-muted-foreground"
          title={dielec.nazov_strojovy ?? ""}
        >
          {dielec.nazov_strojovy || "—"}
        </div>
      </div>

      {/* Malý nákres — aby sa hrany klikali podľa polohy, nie podľa názvu */}
      {dielec.masiv ? (
        <MiniNakresZaoblenia zaoblenie={zaoblenie} onToggle={prepniZaoblenie} />
      ) : (
        <MiniNakres hrany={hrany} onToggle={toggle} />
      )}

      <div className="flex min-w-0 items-center gap-2 overflow-hidden">
        {dielec.masiv
          ? STRANY_HRAN.map((key) => (
              <ZaoblenieChip
                key={key}
                strana={key}
                zapnute={zaoblenie[key]}
                onToggle={() => prepniZaoblenie(key)}
              />
            ))
          : STRANY_HRAN.map((key) => (
              <EdgeChipy
                key={key}
                strana={key}
                edge={hrany[key]}
                onToggle={() => toggle(key)}
                onHrubka={(mm) => void priradHrubku(key, mm)}
                stav={dielec.stav_hran?.[key]}
              />
            ))}
      </div>
    </div>
  );
}

/** Strany mini nákresu — rovnaké pre hrany aj zaoblenie. */
const MINI_STRANY: {
  key: keyof HranyState;
  styl: React.CSSProperties;
  popis: string;
}[] = [
  { key: "x1", styl: { top: 0, left: 0, right: 0, height: 5 }, popis: "hore" },
  { key: "x2", styl: { bottom: 0, left: 0, right: 0, height: 5 }, popis: "dole" },
  { key: "y1", styl: { top: 0, bottom: 0, left: 0, width: 5 }, popis: "vľavo" },
  { key: "y2", styl: { top: 0, bottom: 0, right: 0, width: 5 }, popis: "vpravo" },
];

/** Čiarkovaná čiara zaoblenia na vonkajšom okraji strany mini nákresu. */
const CIARA_ZAOBLENIA: Record<keyof ZaoblenieState, string> = {
  x1: "2px 0 0 0",
  x2: "0 0 2px 0",
  y1: "0 0 0 2px",
  y2: "0 2px 0 0",
};

/**
 * Mini nákres masívu (KOLO 168) — klik na stranu ju zaoblí; zaoblená
 * strana je čiarkovaná, ako v plnom nákrese.
 */
function MiniNakresZaoblenia({
  zaoblenie,
  onToggle,
}: {
  zaoblenie: ZaoblenieState;
  onToggle: (key: keyof ZaoblenieState) => void;
}): JSX.Element {
  return (
    <div
      className="relative shrink-0 rounded border-2 border-foreground/40 bg-background-alt/60"
      style={{ width: MINI_W, height: MINI_H }}
      title="Masív sa neolepuje — klik na stranu ju zaoblí"
    >
      {MINI_STRANY.map(({ key, styl, popis }) => (
        <button
          key={key}
          type="button"
          onClick={() => onToggle(key)}
          aria-label={`Zaobliť stranu ${key.toUpperCase()}`}
          title={`${key.toUpperCase()} (${popis}) — ${zaoblenie[key] ? "zaoblená" : "bez zaoblenia"}`}
          className={cn(
            "absolute cursor-pointer transition-colors",
            zaoblenie[key]
              ? cn("border-dashed", ZAOBLENIE_FARBA.line)
              : "bg-transparent hover:bg-accent",
          )}
          style={{
            ...styl,
            ...(zaoblenie[key] ? { borderWidth: CIARA_ZAOBLENIA[key] } : {}),
          }}
        />
      ))}
    </div>
  );
}

/** Prepínač zaoblenia jednej strany masívu (namiesto checkboxu a čipov). */
function ZaoblenieChip({
  strana,
  zapnute,
  onToggle,
}: {
  strana: string;
  zapnute: boolean;
  onToggle: () => void;
}): JSX.Element {
  const label = strana.toUpperCase();
  return (
    <div className="flex shrink-0 items-center gap-1">
      <span className="w-5 font-mono text-[10px] text-muted-foreground">
        {label}
      </span>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={zapnute}
        aria-label={`Zaoblenie ${label}`}
        title={
          zapnute
            ? `${label} sa zaobľuje na olepovačke — klik: bez zaoblenia`
            : `Zaobliť ${label} na olepovačke (masív sa neolepuje)`
        }
        className={cn(
          "rounded border px-1 py-0 text-[9px] leading-4 transition-colors",
          zapnute
            ? ZAOBLENIE_FARBA.chip
            : "border-input text-muted-foreground hover:bg-accent",
        )}
      >
        zaoblenie
      </button>
    </div>
  );
}

/**
 * Obdĺžnik 56×32 so 4 klikateľnými stranami. Farba strany = hrúbka hrany,
 * rovnaká paleta ako v plnom nákrese (`HRANA_FARBY`), takže lišta a pás
 * hovoria tou istou rečou.
 */
function MiniNakres({
  hrany,
  onToggle,
}: {
  hrany: HranyState;
  onToggle: (key: keyof HranyState) => void;
}): JSX.Element {
  const info = {
    x1: useEdgeInfo(hrany.x1),
    x2: useEdgeInfo(hrany.x2),
    y1: useEdgeInfo(hrany.y1),
    y2: useEdgeInfo(hrany.y2),
  };
  return (
    <div
      className="relative shrink-0 rounded border-2 border-foreground/40 bg-background-alt/60"
      style={{ width: MINI_W, height: MINI_H }}
    >
      {MINI_STRANY.map(({ key, styl, popis }) => {
        const e = hrany[key];
        const zapnuta = e.auto || !!e.id;
        const bucket = hrubkaBucket(info[key].hrubka) ?? "0.5";
        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggle(key)}
            aria-label={`Zapnúť / vypnúť hranu ${key.toUpperCase()}`}
            title={`${key.toUpperCase()} (${popis}) — ${zapnuta ? info[key].label : "bez hrany"}`}
            className={cn(
              "absolute cursor-pointer rounded-sm transition-colors",
              zapnuta
                ? (HRANA_FARBY[bucket]?.line ?? "bg-sky-500")
                : "bg-transparent hover:bg-accent",
            )}
            style={styl}
          />
        );
      })}
    </div>
  );
}

/** Checkbox + názov strany + chipy hrúbky pre jednu hranu. */
function EdgeChipy({
  strana,
  edge,
  onToggle,
  onHrubka,
  stav,
}: {
  strana: string;
  edge: EdgeState;
  onToggle: () => void;
  onHrubka: (mm: string) => void;
  /** Stav uloženej hrany z backendu (`zla_sirka`…), KOLO 135. */
  stav?: string;
}): JSX.Element {
  const info = useEdgeInfo(edge);
  const zlaSirka = stav === STAV_ZLA_SIRKA;
  const zapnuta = edge.auto || !!edge.id;
  const active = hrubkaBucket(info.hrubka);
  const nahradna = jeNahradnaHrubka(info.hrubka);
  const label = strana.toUpperCase();
  return (
    <div
      className="flex shrink-0 items-center gap-1"
      title={
        zlaSirka
          ? `${info.label} — hrana je úzka na hrúbku dosky, vymeň ju`
          : info.label
      }
    >
      <input
        type="checkbox"
        checked={zapnuta}
        onChange={onToggle}
        aria-label={`Zapnúť / vypnúť hranu ${label}`}
        className="h-3.5 w-3.5 cursor-pointer accent-primary"
      />
      <span
        className={cn(
          "w-5 font-mono text-[10px]",
          zlaSirka ? "font-semibold text-destructive" : "text-muted-foreground",
        )}
      >
        {label}
        {zlaSirka ? (
          <span role="img" aria-label={`Hrana ${label} je úzka`}>
            !
          </span>
        ) : null}
      </span>
      {zapnuta ? (
        <div className="flex gap-0.5">
          {QUICK_HRUBKY.map((mm) => (
            <button
              key={mm}
              type="button"
              onClick={() => onHrubka(mm)}
              aria-label={`${label} hrana ${mm} mm`}
              title={
                active === mm && nahradna
                  ? `V dekore je ${info.hrubka} mm — ${mm} mm tam nie je`
                  : `Priradiť ${mm} mm hranu`
              }
              className={cn(
                "rounded border px-1 py-0 font-mono text-[9px] leading-4 transition-colors",
                active === mm
                  ? (HRANA_FARBY[mm]?.chip ??
                      "bg-primary text-primary-foreground border-primary")
                  : "border-input text-muted-foreground hover:bg-accent",
              )}
            >
              {mm}
              {active === mm && nahradna ? "≠" : null}
            </button>
          ))}
        </div>
      ) : null}
      {zapnuta ? <HranaOdkaz odkaz={info.odkaz} label={label} /> : null}
    </div>
  );
}
